import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Stack,
    MenuItem,
} from "@mui/material";

import { auth, db } from "../firebase";
import {
    collection,
    addDoc,
    Timestamp,
    query,
    where,
    onSnapshot,
    doc,
    getDocs,
    deleteDoc,
    setDoc,
    orderBy,
} from "firebase/firestore";

export default function CaptainAssignSeating() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
    });

    const [propertyId, setPropertyId] = useState("");
    const [categories, setCategories] = useState([]);
    const [captains, setCaptains] = useState([]);
    const [assigned, setAssigned] = useState([]);
    const [search, setSearch] = useState("");

    const [openAdd, setOpenAdd] = useState(false);
    const [seatId, setSeatId] = useState("");
    const [captainId, setCaptainId] = useState("");

    /* ================= PROPERTY ID ================= */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) return;
            const snap = await getDocs(
                query(collection(db, "authroles"), where("uid", "==", user.uid))
            );
            if (snap.empty) {
                Toast.fire({ icon: "error", title: "Property not assigned" });
                return;
            }
            const data = snap.docs[0].data();
            setPropertyId(data.propertyId);
        });

        return () => unsub();
    }, []);

    /* ================= FETCH SEATING CATEGORIES ================= */
    useEffect(() => {
        if (!propertyId) return;
        const q = query(
            collection(db, "seatingcats"),
            where("propertyId", "==", propertyId)
        );
        return onSnapshot(q, (snap) => {
            setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });
    }, [propertyId]);

    /* ================= FETCH CAPTAINS ================= */
    useEffect(() => {
        if (!propertyId) return;

        const fetchCaptains = async () => {
            const q = query(
                collection(db, "authroles"),
                where("propertyId", "==", propertyId),
                where("role", "==", "Captain") // ✅ Only captains
            );
            const snap = await getDocs(q);
            const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setCaptains(data);
        };
        fetchCaptains();
    }, [propertyId]);

    /* ================= FETCH ASSIGNED CAPTAINS ================= */
    useEffect(() => {
        if (!propertyId) return;
        const q = query(
            collection(db, "captainassign"),
            where("propertyId", "==", propertyId),
            orderBy("createdAt", "asc")
        );
        return onSnapshot(q, (snap) => {
            const data = snap.docs.map((d) => ({
                id: d.id,
                ...d.data(),
                seatname:
                    categories.find((c) => c.id === d.data().seatId)?.catname || "",
                captainname:
                    captains.find((c) => c.id === d.data().captainId)?.name || "",
            }));
            setAssigned(data);
        });
    }, [propertyId, categories, captains]);

    /* ================= ADD ASSIGNMENT ================= */
    const handleAdd = async () => {
        if (!seatId || !captainId) {
            Toast.fire({ icon: "error", title: "Select seating & captain" });
            return;
        }

        // Check if already assigned
        const q = query(
            collection(db, "captainassign"),
            where("propertyId", "==", propertyId),
            where("seatId", "==", seatId),
            where("captainId", "==", captainId)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
            Toast.fire({ icon: "error", title: "Captain already assigned to this seat" });
            return;
        }

        await addDoc(collection(db, "captainassign"), {
            propertyId,
            seatId,
            captainId,
            createdAt: Timestamp.now(),
        });

        setSeatId("");
        setCaptainId("");
        setOpenAdd(false);
        Toast.fire({ icon: "success", title: "Captain assigned successfully" });
    };

    /* ================= DELETE ASSIGNMENT ================= */
    const handleDelete = async (id) => {
        await deleteDoc(doc(db, "captainassign", id));
        Toast.fire({ icon: "success", title: "Assignment removed" });
    };

    /* ================= FILTER ================= */
    const filtered = assigned.filter((a) =>
        a.captainname.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Box className=" p-3">
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" mb={2}>
                        <Typography variant="h6">Captain Assignments</Typography>
                        <Button variant="contained" onClick={() => setOpenAdd(true)}>
                            Assign Captain
                        </Button>
                    </Stack>

                    <TextField
                        size="small"
                        placeholder="Search captain..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Seating</TableCell>
                                    <TableCell>Captain</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.map((row, i) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell>{row.seatname}</TableCell>
                                        <TableCell>{row.captainname}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(row.id)}
                                            >
                                                Remove
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* ADD ASSIGNMENT DIALOG */}
            <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth>
                <DialogTitle>Assign Captain</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth size="small"
                        select
                        label="Seating Category"
                        value={seatId}
                        onChange={(e) => setSeatId(e.target.value)}
                        sx={{ mt: 2 }}
                    >
                        {categories.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.catname}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth size="small"
                        select
                        label="Captain"
                        value={captainId}
                        onChange={(e) => setCaptainId(e.target.value)}
                        sx={{ mt: 2 }}
                    >
                        {captains.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAdd}>
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}